import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button, ComponentSync, LL_H_C, LL_V_L, Show } from '@nu-art/thunderstorm-frontend/index';
import { exists, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { ICONS } from '@app/styles';
import { ModuleFE_Spotlight } from '../../../modules/ModuleFE_Spotlight/index.js';
import './Renderer_Support.scss';
import { FadeScrollWrapper } from '@app/styles';
import { ModuleFE_Advisor_FAQ } from '@app/advisor-frontend';
import { FAQType_Child, FAQType_Parent, FAQType_VideoChild } from '@app/advisor-ui-shared';
import { AdvisorConfigKeyFE_FAQParentOrder } from '@app/advisor-frontend';
import { ModuleFE_ScrollBar } from '@app/styles/frontend/modules/ModuleFE_ScrollBar/ModuleFE_ScrollBar';
import { supportType_Feedback } from '@app/advisor-shared';
import { ModuleFE_AdvisorAnalytics } from '../../../_analytics/consts.js';
import { Component_SupportReport } from '../../dialogs/support-report-dialog/Component_SupportReport.js';
import { ModuleFE_AdvisorMini } from '../../../modules/ModuleFE_AdvisorMini.js';
import { PanelKey_Home } from '../../../_shared.js';
import { Render_Support_Video } from './Render_Support_Video.js';
export class Renderer_Support extends ComponentSync {
    //######################### Life Cycle #########################
    componentDidMount() {
        //Report analytics event
        ModuleFE_AdvisorAnalytics.trigger.faq.faq_open();
    }
    componentDidUpdate() {
        ModuleFE_ScrollBar.updateScrollBar(this.props.scrollRef);
    }
    //######################### Logic #########################
    getParentFAQs = () => {
        const parentFAQs = ModuleFE_Advisor_FAQ.cache.filter(faq => faq.type === FAQType_Parent);
        const parentOrder = AdvisorConfigKeyFE_FAQParentOrder.get() ?? [];
        sortArray(parentFAQs, parent => parentOrder.indexOf(parent._id));
        return parentFAQs;
    };
    setSelectedFAQId = (id) => {
        this.setState({ selectedQuestionId: id });
        //Report analytics event
        const faq = ModuleFE_Advisor_FAQ.cache.unique(id);
        if (!exists(faq))
            return;
        ModuleFE_AdvisorAnalytics.trigger.faq.faq_questionOpen({ questionId: faq._id, questionLabel: faq.label });
    };
    formatAnswer = (texts) => {
        const parts = texts.split(/(\*.*?\*|\{.*?\})/g);
        return parts.map((part, index) => {
            if (part.startsWith('*') && part.endsWith('*')) { //Part needs bold formatting
                return _jsx("b", { children: part.slice(1, -1) }, index);
            }
            else if (part.startsWith('{') && part.endsWith('}')) { //Part needs icon formatting
                const iconKey = part.slice(1, -1);
                const ICON = ICONS[iconKey];
                if (!ICON)
                    return _jsx("b", { className: 'error', children: "[ICON N/A]" });
                return _jsx(ICON.component, { asSpan: true }, index);
            }
            //No formatting
            return part;
        });
    };
    getFAQIcon = (faq) => {
        switch (faq.type) {
            case FAQType_Child:
                return ICONS.advisor_v5_buttonArrow;
            case FAQType_VideoChild:
                return ICONS.advisor_v5_video;
        }
    };
    //######################### Render #########################
    render() {
        return _jsxs(_Fragment, { children: [this.render_Header(), this.render_Content()] });
    }
    render_Header = () => {
        const selectedQuestion = exists(this.state.selectedQuestionId);
        return _jsxs(LL_H_C, { className: 'support__header', children: [_jsxs(Show, { children: [_jsx(Show.If, { condition: selectedQuestion, children: _jsx(ICONS.advisor_v5_arrow.component, { onClick: () => this.setSelectedFAQId() }) }), _jsx(Show.Else, { children: "Support" })] }), _jsxs(LL_H_C, { className: 'support__header__tail', children: [_jsx(Button, { variant: 'header-button', onClick: () => Component_SupportReport.show({ type: supportType_Feedback }), children: "Send Feedback" }), _jsx(ICONS.advisor_avn_close.component, { onClick: () => {
                                if (ModuleFE_AdvisorMini.getIsMiniActive())
                                    return ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
                                ModuleFE_Spotlight.closeSpotlight();
                            } })] })] });
    };
    render_Content = () => {
        if (!this.state.selectedQuestionId)
            return this.render_Sections();
        return this.render_FAQ();
    };
    //######################### Render - Sections #########################
    render_Sections = () => {
        const parentFAQs = this.getParentFAQs();
        return _jsx(FadeScrollWrapper, { scrollRef: this.props.scrollRef, className: 'support__sections', children: parentFAQs.map(faq => {
                return _jsxs(LL_V_L, { className: 'support__section', children: [_jsx("div", { className: 'support__section__title', children: faq.label }), _jsx(LL_V_L, { className: 'support__section__children', children: faq.childrenIds.map((id, index) => this.render_Section_Child(id, index !== faq.childrenIds.length - 1)) })] }, faq._id);
            }) });
    };
    render_Section_Child = (childId, renderDivider) => {
        const faq = ModuleFE_Advisor_FAQ.cache.unique(childId);
        if (!faq)
            throw new MUSTNeverHappenException(`Could not find FAQ for id ${childId}`);
        const Icon = this.getFAQIcon(faq);
        const label = faq.type === FAQType_VideoChild ? (faq.uiLabel ?? faq.label) : faq.label;
        return _jsxs(React.Fragment, { children: [_jsxs(LL_H_C, { className: 'support__section__child', onClick: () => this.setSelectedFAQId(childId), children: [_jsx("div", { className: 'support__section__child-title', children: label }), _jsx(Icon.component, {})] }), renderDivider && _jsx("div", { className: 'support__section__children-divider' })] }, childId);
    };
    //######################### Render - FAQ #########################
    render_FAQ = () => {
        if (!this.state.selectedQuestionId)
            return;
        const selectedFAQ = ModuleFE_Advisor_FAQ.cache.unique(this.state.selectedQuestionId);
        if (!selectedFAQ)
            throw new MUSTNeverHappenException(`Could not get FAQ for id ${this.state.selectedQuestionId}`);
        switch (selectedFAQ.type) {
            case FAQType_Child:
                return this.render_FAQ_Child(selectedFAQ);
            case FAQType_VideoChild:
                return _jsx(Render_Support_Video, { faq: selectedFAQ });
        }
    };
    render_FAQ_Child = (faq) => {
        const answer = this.formatAnswer(faq.answer);
        return _jsxs(LL_V_L, { className: 'support__faq', children: [_jsx("div", { className: 'support__faq__question', children: faq.label }), _jsx(FadeScrollWrapper, { scrollRef: this.props.scrollRef, children: _jsx("p", { className: 'support__faq__answer', children: answer }) })] });
    };
}
//# sourceMappingURL=Renderer_Support.js.map